import React from "react";
import BubbleField from "../BubbleField/";

interface CheckUsOutProps {
  imgTopSrc: string;
  imgTopAlt?: string;
  imgMiddleSrc: string; // newly added middle image
  imgMiddleAlt?: string;
  imgBottomSrc: string;
  imgBottomAlt?: string;
  imgVideoSrc?: string; // optional video source
  title: string; // e.g., "COME CHECK US OUT"
  copy: string; // paragraph text
  parallax?: boolean; // enable subtle desktop parallax
  reverse?: boolean; // reverse desktop order
  variant?: "horizontal" | "tiered"; // layout style
  showBubbles?: boolean; // toggle decorative bubbles
  bubbleAnimation?: "none" | "float" | "drift"; // animation style
  backgroundImg?: string; // optional background image for whole section
  slideInVariant?: 'none' | 'stagger' | 'displace-lite'; // entrance animation style
}

const CheckUsOutComponent: React.FC<CheckUsOutProps> = ({
  imgTopSrc,
  imgTopAlt = "",
  imgMiddleSrc,
  imgMiddleAlt = "",
  imgBottomSrc,
  imgBottomAlt = "",
  imgVideoSrc,
  title,
  copy,
  parallax = true,
  reverse = false,
  variant = "horizontal",
  showBubbles = true,
  bubbleAnimation = "float",
  backgroundImg,
  slideInVariant = 'stagger',
}) => {
  const [parallaxXY, setParallaxXY] = React.useState({ x: 0, y: 0 });
  const parallaxRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!parallax) return;
    const el = parallaxRef.current;
    if (!el) return;
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) return;
    let frame: number | null = null;
    const handleMove = (e: MouseEvent) => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const rx = (e.clientX - rect.left) / rect.width; // 0-1
        const ry = (e.clientY - rect.top) / rect.height; // 0-1
        // center origin (-0.5 to 0.5)
        setParallaxXY({ x: rx - 0.5, y: ry - 0.5 });
      });
    };
    el.addEventListener("mousemove", handleMove);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      el.removeEventListener("mousemove", handleMove);
    };
  }, [parallax]);

  // Entrance observer for slide-in animation
  const rootRef = React.useRef<HTMLElement | null>(null);
  const [entered, setEntered] = React.useState(false);
  React.useEffect(() => {
    if (slideInVariant === 'none') return;
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setEntered(true);
        io.disconnect();
      }
    }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [slideInVariant]);

  // Utility to compose animation classes
  const baseHidden = slideInVariant === 'none' ? '' : 'opacity-0 translate-y-10 scale-[1.02]';
  const revealed = 'opacity-100 translate-y-0 scale-100';
  const transitionCommon = 'transition-all duration-[900ms] ease-[cubic-bezier(.19,1,.22,1)]';
  const displaceMask = slideInVariant === 'displace-lite'
    ? (!entered ? 'clip-path-[inset(0_0_100%_0)]' : 'clip-path-[inset(0_0_0%_0)]')
    : '';

  const imageRevealClass = slideInVariant === 'none' ? '' : `${transitionCommon} will-change-transform will-change-opacity ${entered ? revealed : baseHidden}`;
  const textRevealClass = slideInVariant === 'none' ? '' : `${transitionCommon} ${entered ? revealed : baseHidden}`;

  const topStyle: React.CSSProperties = parallax
    ? {
      transform: `translate3d(${parallaxXY.x * -12}px, ${parallaxXY.y * -12
        }px, 0)`,
    }
    : {};
  const middleStyle: React.CSSProperties = parallax
    ? {
      transform: `translate3d(${parallaxXY.x * 16}px, ${parallaxXY.y * 18
        }px, 0)`,
    }
    : {};
  const bottomStyle: React.CSSProperties = parallax
    ? {
      transform: `translate3d(${parallaxXY.x * 10}px, ${parallaxXY.y * 14
        }px, 0)`,
    }
    : {};
  const sectionStyle: React.CSSProperties = {
    backgroundImage: `url(${imgBottomSrc})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };

  return (
    <section
      className="w-full relative overflow-hidden bg-[#231f1f] text-white"
      style={sectionStyle}
      ref={rootRef as any}
    >
      <div className="absolute inset-0 bg-black/90" aria-hidden="true" />
      {showBubbles && (
        <div className="absolute inset-0 z-10 pointer-events-none">
          <BubbleField animation={bubbleAnimation} />
        </div>
      )}
      <div className="relative z-20 mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 md:py-20 lg:py-24">
        <div className="grid items-center gap-10 sm:gap-14 lg:grid-cols-12">
          {/* Images: default order-2 so text shows first on mobile; revert to original order at lg unless reversed */}
          <div
            className={`order-2 mt-12 lg:mt-0 lg:col-span-7 xl:col-span-8 ${reverse ? 'lg:order-2' : 'lg:order-1'} ${reverse ? "direction-rtl" : ""
              } relative`}
          >
            {/* direction-rtl (logical placeholder if future styles) */}
            {/* Wrap actual image content to ensure above bubbles */}
            <div className={`relative z-10 ${displaceMask} ${imageRevealClass}`} style={slideInVariant === 'displace-lite' ? { transition: 'clip-path 1.4s cubic-bezier(.19,1,.22,1), filter 1.4s ease', filter: entered ? 'blur(0px) brightness(1)' : 'blur(8px) brightness(0.6)' } : {}}>
              {/* Mobile/Tablet: enhanced triple images (stack or row) */}
              <div className="flex flex-col gap-8 sm:grid sm:grid-cols-3 sm:gap-8 lg:hidden">
                {/* Video Player (if provided) */}
                {imgVideoSrc && (
                  <div className="relative group sm:col-span-3">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-sow-gold/30 via-sow-darkGold/20 to-sow-gold/30 opacity-0 group-hover:opacity-100 blur-lg transition pointer-events-none" />
                    <video
                      src={imgVideoSrc}
                      autoPlay
                      muted
                      loop
                      controls
                      playsInline
                      controlsList="nodownload"
                      className="h-72 w-full sm:h-80 rounded-3xl object-cover shadow-[0_8px_30px_-4px_rgba(0,0,0,0.65)] ring-2 ring-sow-gold/20 transition duration-500 group-hover:ring-sow-gold/40 relative z-10 pointer-events-auto"
                    />
                  </div>
                )}
                {/* Top - Polaroid style */}
                {imgTopSrc && (
                <div className="relative group">
                  <div className="absolute inset-0 rounded-sm bg-gradient-to-br from-sow-gold/25 via-transparent to-sow-darkGold/30 opacity-0 group-hover:opacity-100 blur-md transition" />
                  <div className="bg-white p-3 rounded-sm shadow-[0_8px_24px_-4px_rgba(0,0,0,0.6)] ring-1 ring-black/5 rotate-[-2deg] transition duration-500 group-hover:rotate-0 group-hover:scale-[1.05] group-hover:shadow-[0_12px_32px_-4px_rgba(0,0,0,0.7)] overflow-hidden">
                    <img
                      src={imgTopSrc}
                      alt={imgTopAlt}
                      loading="lazy"
                      className="h-52 w-full sm:h-64 object-cover"
                    />
                    <div className="h-12 bg-white" />
                  </div>
                </div>
                )}
                {/* Middle - Polaroid style */}
                {imgMiddleSrc && (
                <div className="relative group">
                  <div className="absolute inset-0 rounded-sm bg-gradient-to-br from-sow-darkGold/25 via-transparent to-sow-gold/30 opacity-0 group-hover:opacity-100 blur-md transition" />
                  <div className="bg-white p-3 rounded-sm shadow-[0_8px_24px_-4px_rgba(0,0,0,0.6)] ring-1 ring-black/5 rotate-[1.5deg] transition duration-500 group-hover:rotate-0 group-hover:scale-[1.05] group-hover:shadow-[0_12px_32px_-4px_rgba(0,0,0,0.7)] overflow-hidden">
                    <img
                      src={imgMiddleSrc}
                      alt={imgMiddleAlt}
                      loading="lazy"
                      className="h-52 w-full sm:h-64 object-cover"
                    />
                    <div className="h-12 bg-white" />
                  </div>
                </div>
                )}
                {/* Bottom - Polaroid style */}
                {imgBottomSrc && (
                <div className="relative group">
                  <div className="absolute inset-0 rounded-sm bg-gradient-to-br from-sow-gold/25 via-transparent to-sow-darkGold/30 opacity-0 group-hover:opacity-100 blur-md transition" />
                  <div className="bg-white p-3 rounded-sm shadow-[0_8px_24px_-4px_rgba(0,0,0,0.6)] ring-1 ring-black/5 rotate-[-1deg] transition duration-500 group-hover:rotate-0 group-hover:scale-[1.05] group-hover:shadow-[0_12px_32px_-4px_rgba(0,0,0,0.7)] overflow-hidden">
                    <img
                      src={imgBottomSrc}
                      alt={imgBottomAlt}
                      loading="lazy"
                      className="h-52 w-full sm:h-64 object-cover"
                    />
                    <div className="h-12 bg-white" />
                  </div>
                </div>
                )}
              </div>

              {/* Desktop variants */}
              {variant === "horizontal" && (
                <div
                  ref={parallaxRef}
                  className={`hidden lg:flex relative will-change-transform flex-col ${reverse ? "flex-row-reverse" : ""
                    } items-center gap-10 xl:gap-14 pl-2 pr-2`}
                >
                  {/* Video Player (if provided) - full width at top */}
                  {imgVideoSrc && (
                    <div className="relative w-full mb-6 group/video">
                      <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-sow-gold/25 via-sow-darkGold/15 to-sow-gold/25 opacity-0 group-hover/video:opacity-100 blur-2xl transition duration-500 pointer-events-none" />
                      <div className="relative bg-gradient-to-br from-zinc-900 to-black p-4 rounded-2xl shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8)] ring-2 ring-sow-gold/30">
                        <video
                          src={imgVideoSrc}
                          autoPlay
                          muted
                          loop
                          controls
                          playsInline
                          className="w-full h-[450px] rounded-xl object-cover shadow-[inset_0_2px_12px_rgba(0,0,0,0.4)] transition duration-500 group-hover/video:shadow-[inset_0_2px_12px_rgba(0,0,0,0.2)] relative z-10 pointer-events-auto"
                        />
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-sow-gold to-transparent opacity-60 pointer-events-none" />
                      </div>
                    </div>
                  )}
                  {/* Polaroid images in a row */}
                  <div className={`flex ${reverse ? "flex-row-reverse" : "flex-row"} items-center justify-between gap-10 xl:gap-14 w-full`}>
                    {/* Left (Top) - Polaroid */}
                    {imgTopSrc && (
                    <div
                      style={topStyle}
                      className="relative -mt-8 transition-transform duration-300 ease-out group/image"
                    >
                      <div className="absolute -inset-3 rounded-sm bg-gradient-to-br from-sow-gold/20 via-transparent to-sow-darkGold/20 opacity-0 group-hover/image:opacity-100 blur-lg transition" />
                      <div className="bg-white p-4 rounded-sm shadow-[0_16px_48px_-12px_rgba(0,0,0,0.65)] ring-1 ring-black/5 rotate-[-3deg] transition duration-700 ease-out group-hover/image:rotate-0 group-hover/image:scale-[1.08] group-hover/image:shadow-[0_24px_64px_-10px_rgba(0,0,0,0.75)] overflow-hidden">
                        <img
                          src={imgTopSrc}
                          alt={imgTopAlt}
                          loading="lazy"
                          className="h-[340px] w-auto object-cover"
                        />
                        <div className="h-16 bg-white" />
                      </div>
                    </div>
                    )}
                    {/* Center (Middle focal) - Polaroid */}
                    {imgMiddleSrc && (
                    <div
                      style={middleStyle}
                      className="relative z-10 transition-transform duration-300 ease-out group/image"
                    >
                      <div className="absolute -inset-4 rounded-sm bg-gradient-to-br from-sow-darkGold/25 via-transparent to-sow-gold/25 opacity-0 group-hover/image:opacity-100 blur-xl transition" />
                      <div className="bg-white p-5 rounded-sm shadow-[0_20px_70px_-14px_rgba(0,0,0,0.7)] ring-1 ring-black/5 rotate-[2deg] transition duration-700 ease-out group-hover/image:rotate-0 group-hover/image:scale-[1.1] group-hover/image:shadow-[0_28px_90px_-12px_rgba(0,0,0,0.8)] overflow-hidden">
                        <img
                          src={imgMiddleSrc}
                          alt={imgMiddleAlt}
                          loading="lazy"
                          className="h-[350px] w-auto object-cover"
                        />
                        <div className="h-20 bg-white" />
                      </div>
                    </div>
                    )}
                    {/* Right (Bottom) - Polaroid */}
                    {imgBottomSrc && (
                    <div
                      style={bottomStyle}
                      className="relative mt-8 transition-transform duration-300 ease-out group/image"
                    >
                      <div className="absolute -inset-3 rounded-sm bg-gradient-to-br from-sow-gold/18 via-transparent to-sow-darkGold/18 opacity-0 group-hover/image:opacity-100 blur-lg transition" />
                      <div className="bg-white p-4 rounded-sm shadow-[0_18px_60px_-14px_rgba(0,0,0,0.68)] ring-1 ring-black/5 rotate-[-1.5deg] transition duration-700 ease-out group-hover/image:rotate-0 group-hover/image:scale-[1.08] group-hover/image:shadow-[0_24px_75px_-12px_rgba(0,0,0,0.78)] overflow-hidden">
                        <img
                          src={imgBottomSrc}
                          alt={imgBottomAlt}
                          loading="lazy"
                          className="h-[300px] w-auto object-cover"
                        />
                        <div className="h-16 bg-white" />
                      </div>
                    </div>
                    )}
                  </div>
                </div>
              )}
              {variant === "tiered" && (
                <div
                  ref={parallaxRef}
                  className="hidden lg:flex relative will-change-transform flex-col gap-10 xl:gap-14"
                >
                  {/* Video Player (if provided) - full width at top */}
                  {imgVideoSrc && (
                    <div className="relative w-full group/video">
                      <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-sow-gold/25 via-sow-darkGold/15 to-sow-gold/25 opacity-0 group-hover/video:opacity-100 blur-2xl transition duration-500 pointer-events-none" />
                      <div className="relative bg-gradient-to-br from-zinc-900 to-black p-4 rounded-2xl shadow-[0_20px_80px_-20px_rgba(0,0,0,0.8)] ring-2 ring-sow-gold/30">
                        <video
                          src={imgVideoSrc}
                          autoPlay
                          muted
                          loop
                          controls
                          playsInline
                          className="w-full h-[450px] rounded-xl object-cover shadow-[inset_0_2px_12px_rgba(0,0,0,0.4)] transition duration-500 group-hover/video:shadow-[inset_0_2px_12px_rgba(0,0,0,0.2)] relative z-10 pointer-events-auto"
                        />
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-sow-gold to-transparent opacity-60 pointer-events-none" />
                      </div>
                    </div>
                  )}
                  {/* Tiered polaroid layout */}
                  <div
                    className={`grid ${reverse ? "grid-cols-[420px_1fr]" : "grid-cols-[1fr_420px]"
                      } gap-10 xl:gap-14 items-center`}
                  >
                    {/* Large focal (middle image) - Polaroid */}
                    {imgMiddleSrc && (
                    <div
                      className={`${reverse ? "order-2" : "order-1"
                        } relative transition-transform duration-300 ease-out group/image`}
                      style={middleStyle}
                    >
                      <div className="absolute -inset-4 rounded-sm bg-gradient-to-br from-sow-darkGold/30 via-transparent to-sow-gold/30 opacity-0 group-hover/image:opacity-100 blur-xl transition" />
                      <div className="bg-white p-6 rounded-sm shadow-[0_28px_100px_-20px_rgba(0,0,0,0.75)] ring-1 ring-black/5 rotate-[1deg] transition duration-700 ease-out group-hover/image:rotate-0 group-hover/image:scale-[1.06] group-hover/image:shadow-[0_36px_120px_-18px_rgba(0,0,0,0.85)] overflow-hidden">
                        <img
                          src={imgMiddleSrc}
                          alt={imgMiddleAlt}
                          loading="lazy"
                          className="h-[440px] w-auto object-cover"
                        />
                        <div className="h-24 bg-white" />
                      </div>
                    </div>
                    )}
                    {/* Column with two stacked polaroid images (top & bottom) */}
                    <div
                      className={`${reverse ? "order-1" : "order-2"
                        } flex flex-col gap-10 relative`}
                    >
                      {imgTopSrc && (
                      <div
                        style={topStyle}
                        className="relative self-start -mr-6 transition-transform duration-300 ease-out group/image"
                      >
                        <div className="absolute -inset-3 rounded-sm bg-gradient-to-br from-sow-gold/22 via-transparent to-sow-darkGold/22 opacity-0 group-hover/image:opacity-100 blur-lg transition" />
                        <div className="bg-white p-4 rounded-sm shadow-[0_18px_56px_-14px_rgba(0,0,0,0.66)] ring-1 ring-black/5 rotate-[-2.5deg] transition duration-700 ease-out group-hover/image:rotate-0 group-hover/image:scale-[1.08] group-hover/image:shadow-[0_22px_68px_-12px_rgba(0,0,0,0.74)] overflow-hidden">
                          <img
                            src={imgTopSrc}
                            alt={imgTopAlt}
                            loading="lazy"
                            className="h-[280px] w-auto object-cover"
                          />
                          <div className="h-14 bg-white" />
                        </div>
                      </div>
                      )}
                      {imgBottomSrc && (
                      <div
                        style={bottomStyle}
                        className="relative self-end mt-4 transition-transform duration-300 ease-out group/image"
                      >
                        <div className="absolute -inset-3 rounded-sm bg-gradient-to-br from-sow-darkGold/22 via-transparent to-sow-gold/22 opacity-0 group-hover/image:opacity-100 blur-lg transition" />
                        <div className="bg-white p-4 rounded-sm shadow-[0_20px_68px_-16px_rgba(0,0,0,0.68)] ring-1 ring-black/5 rotate-[2deg] transition duration-700 ease-out group-hover/image:rotate-0 group-hover/image:scale-[1.08] group-hover/image:shadow-[0_26px_82px_-14px_rgba(0,0,0,0.76)] overflow-hidden">
                          <img
                            src={imgBottomSrc}
                            alt={imgBottomAlt}
                            loading="lazy"
                            className="h-[260px] w-auto object-cover"
                          />
                          <div className="h-16 bg-white" />
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* end z-10 wrapper */}
          </div>

          {/* Text: default order-1 (before images on mobile), large screen order controlled by reverse prop */}
          <div className={`order-1 lg:col-span-5 xl:col-span-4 ${reverse ? 'lg:order-1' : 'lg:order-2'} lg:pr-4 ${textRevealClass} ${displaceMask}`} style={slideInVariant === 'displace-lite' ? { transition: 'clip-path 1.3s cubic-bezier(.19,1,.22,1) 120ms, filter 1.2s ease 120ms', filter: entered ? 'blur(0px) opacity(1)' : 'blur(6px) opacity(.6)' } : {}}>
            <div className="h-[2px] w-28 bg-gradient-to-r from-transparent via-sow-gold/80 to-transparent mb-5 sm:w-40" />
            <h2 className="text-4xl font-extrabold leading-tight tracking-wide sm:text-5xl md:text-6xl lg:text-7xl drop-shadow-[0_4px_18px_rgba(0,0,0,0.6)]">
              {title}
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-zinc-200/90 sm:mt-7 sm:text-xl md:text-[1.35rem] lg:text-[1.5rem] xl:text-[1.55rem] tracking-wide lg:leading-snug">
              {copy}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CheckUsOutComponent;
